import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XIcon, CheckCircleIcon, ListChecksIcon, SearchIcon, PhoneIcon, ClipboardCheckIcon, TrendingUpIcon, AlertCircleIcon, LightbulbIcon, AwardIcon, ArrowRightIcon, ArrowLeftIcon, FileTextIcon, TargetIcon, UserCheckIcon, BriefcaseIcon, ShieldCheckIcon, MessageSquareIcon } from 'lucide-react';
type QuestStep = {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  gradient: string;
  content: {
    tips: string[];
    warnings?: string[];
    checklist?: string[];
  };
};
export function ProspectingTutorial() {
  const navigate = useNavigate();
  const [currentQuest, setCurrentQuest] = useState(0);
  const [completedQuests, setCompletedQuests] = useState<string[]>([]);
  const quests: QuestStep[] = [{
    id: 'intro',
    title: 'Bun venit în Ghidul Prospectării',
    description: 'Învață cum să transformi anunțuri în colaborări și proprietăți în portofoliu',
    icon: ListChecksIcon,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-600',
    content: {
      tips: ['Prospectarea este procesul de identificare și contactare a proprietarilor din anunțuri', 'Un agent profesionist procesează 20-30 leaduri pe zi pentru rezultate constante', 'Rata de conversie medie: 20-30% din leaduri → colaborări → 40-50% în portofoliu', 'Sistemul nostru te ghidează prin 4 pași: Verificare → Apel → Colaborare → Portofoliu', 'Consistența este cheia - prospectează zilnic pentru un flux constant de proprietăți']
    }
  }, {
    id: 'list-generation',
    title: 'Sistemul Nostru de Liste',
    description: 'Cum agenția îți oferă liste de calitate gata de procesat',
    icon: SearchIcon,
    color: 'emerald',
    gradient: 'from-emerald-500 to-green-600',
    content: {
      tips: ['✅ AGENȚIA SE OCUPĂ DE TOT: Nu trebuie să îți faci griji despre găsirea proprietăților', '🎯 FILTRARE AUTOMATĂ: Echipa noastră filtrează zilnic sute de anunțuri și îți oferă doar cele de calitate', '📊 DATE INDEXATE: Fiecare lead vine cu informații esențiale deja extrase (zonă, preț, camere, telefon)', '🔍 VERIFICARE DUBLURI: Eliminăm automat anunțurile duplicate și proprietățile deja în sistem', '📱 SURSE PREMIUM: Monitorizăm OLX, Storia, Imobiliare.ro și alte platforme pentru tine', '⏰ LISTE PROASPETE: Primești liste noi zilnic cu anunțuri recente (<3 zile)', '📈 REȚETĂ DE SUCCES: Urmează ghidul nostru și vei vedea rezultate consistente', '💪 TU TE CONCENTREZI PE EXECUȚIE: Rolul tău e să procesezi listele și să obții colaborări'],
      warnings: ['NU pierde timp căutând proprietăți - concentrează-te pe prelucrarea listelor', 'NU te descuraja dacă o listă nu aduce rezultate - consistența e cheia', 'NU sări peste liste - fiecare lead e o oportunitate'],
      checklist: ['Solicită listă nouă zilnic (buton "Solicită Listă")', 'Verifică că ai primit lista (notificare în sistem)', 'Setează target zilnic: minim 20 leaduri procesate', 'Urmează procesul: Verificare → Apel → Colaborare', 'Fii constant - procesează liste în fiecare zi lucrătoare', 'Raportează rezultatele săptămânal pentru optimizare']
    }
  }, {
    id: 'step1-verification',
    title: 'STEP 1: Verificarea Anunțului',
    description: 'Primul pas - verifică validitatea și oportunitatea',
    icon: ShieldCheckIcon,
    color: 'purple',
    gradient: 'from-purple-500 to-pink-600',
    content: {
      tips: ['🔍 VERIFICĂ ANUNȚUL: Click pe link, citește descrierea completă', '📋 CHECKLIST RAPID: Poze reale? Descriere detaliată? Contact vizibil?', '🏢 VERIFICĂ COLABORARE EXISTENTĂ: Caută adresa în CRM - există deja?', '❌ ANUNȚ INVALID: Fără poze, descriere vagă, contact suspect → Marchează "Anunț invalid"', '✅ ANUNȚ VALID: Poze OK, descriere clară, contact real → Marchează "De prospectat"', '⚠️ COLABORARE EXISTENTĂ: Găsești proprietatea în CRM → Marchează "Există colaborare"', '⏱️ TIMP MEDIU: 30-60 secunde per lead pentru verificare eficientă'],
      warnings: ['NU sări peste verificare - economisești timp evitând apeluri inutile', 'NU presupune - verifică ÎNTOTDEAUNA în CRM dacă există colaborare', 'NU contacta proprietăți cu colaborare activă - riști conflicte'],
      checklist: ['Deschide linkul anunțului în tab nou', 'Verifică calitatea pozelor (reale vs. stock)', 'Citește descrierea (completă vs. vagă)', 'Caută adresa în CRM (există colaborare?)', 'Notează detalii importante (etaj, an construcție, utilități)', 'Marchează decizia: De prospectat / Anunț invalid / Există colaborare']
    }
  }, {
    id: 'step2-call',
    title: 'STEP 2: Apelul de Prospectare',
    description: 'Tehnici profesionale pentru primul contact cu proprietarul',
    icon: PhoneIcon,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    content: {
      tips: ['📞 SCRIPT DESCHIDERE: "Bună ziua, sunt [Nume] agent imobiliar. V-am sunat în legătură cu anunțul dumneavoastră pentru apartamentul din [Zonă]."', '🎯 OBIECTIV: Stabilește dacă proprietarul colaborează cu agenție și în ce condiții', '💰 ÎNTREBĂRI CHEIE: "Colaborați cu o agenție?", "Ce comision oferiți?", "Aveți exclusivitate?"', '✅ COLABOREAZĂ: Comision OK (50%+), fără exclusivitate → Programează întâlnire', '❌ NU COLABOREAZĂ: Refuz categoric, comision prea mic (<30%) → Notează și treci mai departe', '🔄 NU RĂSPUNDE: Încearcă de 3 ori în intervale diferite (dimineață, prânz, seară)', '📝 NOTEAZĂ TOTUL: Răspuns, ton, obiecții, disponibilitate - detaliile contează'],
      warnings: ['NU te descuraja de refuzuri - sunt parte din proces (70% vor refuza)', 'NU argumenta sau insistă excesiv - respectă decizia proprietarului', 'NU uita să notezi motivul refuzului - învață din fiecare interacțiune'],
      checklist: ['Verifică că ai liniște și semnal bun', 'Deschide CRM-ul pentru notițe în timp real', 'Zâmbește înainte de apel (se aude în voce)', 'Prezintă-te profesional (nume + agenție)', 'Menționează anunțul specific (zonă, camere)', 'Pune cele 3 întrebări cheie', 'Notează răspunsul și următorul pas', 'Marchează status: Colaborează / Nu colaborează / Nu răspunde']
    }
  }, {
    id: 'step3-collaboration',
    title: 'STEP 3: Stabilirea Colaborării',
    description: 'Transformă interesul în colaborare concretă',
    icon: UserCheckIcon,
    color: 'rose',
    gradient: 'from-rose-500 to-pink-600',
    content: {
      tips: ['📅 PROGRAMEAZĂ ÎNTÂLNIRE: "Perfect! Când aveți timp să trecem pe la proprietate? Mâine dimineață sau după-amiază?"', '📋 PREGĂTEȘTE DOCUMENTELE: Contract mandat, fișă proprietate, calculator comision', '🏠 LA ÎNTÂLNIRE: Vizitează proprietatea, fotografiază, notează detalii', '📝 SEMNEAZĂ MANDATUL: Explică beneficiile, clarifică comisionul, semnează', '📸 CREEAZĂ ANUNȚ: Poze profesionale, descriere detaliată, preț competitiv', '🆔 GENEREAZĂ ID PROPRIETATE: Sistemul creează automat ID unic (ex: P14433)', '✅ ADAUGĂ ÎN PORTOFOLIU: Proprietatea devine activă în portofoliul tău'],
      warnings: ['NU programa întâlniri fără să confirmi cu 2h înainte - riști să pierzi timpul', 'NU uita documentele - arată neprofesionism', 'NU promite ce nu poți livra - reputația e totul'],
      checklist: ['Programează întâlnire în maxim 48h', 'Confirmă cu proprietarul cu 2h înainte', 'Pregătește contract mandat + fișă proprietate', 'Vizitează și fotografiază proprietatea', 'Semnează mandatul (minim 3 luni)', 'Creează anunț profesional', 'Generează ID proprietate în sistem', 'Marchează "Adăugat în portofoliu"']
    }
  }, {
    id: 'objections',
    title: 'Gestionarea Obiecțiilor Proprietari',
    description: 'Răspunsuri la obiecțiile cele mai frecvente',
    icon: MessageSquareIcon,
    color: 'indigo',
    gradient: 'from-indigo-500 to-blue-600',
    content: {
      tips: ['💬 "Comisionul e prea mare" → "Înțeleg. Să vedem ce include: marketing profesional, verificare chiriași, contract legal, suport complet. Comparativ cu riscurile și timpul dumneavoastră, e un investiție mică pentru siguranță."', '🏢 "Am deja agenție" → "Perfect! Dacă cumva colaborarea actuală nu aduce rezultate, rămân la dispoziție. Vă las numărul meu."', '📝 "Nu vreau exclusivitate" → "Înțeleg perfect. Putem colabora și fără exclusivitate. Important e să găsim chiriașul potrivit împreună."', '⏰ "Nu am timp acum" → "Înțeleg. Când ar fi un moment mai bun? Vă sun [zi] la [oră]?"', '🤔 "Mă gândesc" → "Sigur! Despre ce anume vreți să vă gândiți? Poate vă pot ajuta cu informații suplimentare acum."', '💡 Tehnica VALIDARE + SOLUȚIE: Validează emoția → Oferă soluție concretă → Cere următorul pas'],
      warnings: ['NU te certa sau contrazici - validează mai întâi preocuparea', 'NU renunța după prima obiecție - majoritatea proprietarilor obiectează', 'NU lua personal refuzul - e o decizie de business'],
      checklist: ['Ascultă obiecția complet', 'Validează: "Înțeleg perfect..."', 'Clarifică: "Despre ce anume vă referiți?"', 'Oferă soluție sau alternativă', 'Confirmă rezolvarea obiecției', 'Stabilește următorul pas concret']
    }
  }, {
    id: 'system-flow',
    title: 'Cum Funcționează Sistemul',
    description: 'Înțelege statusurile și flow-ul prospectării',
    icon: TrendingUpIcon,
    color: 'cyan',
    gradient: 'from-cyan-500 to-blue-600',
    content: {
      tips: ['1️⃣ VERIFICARE (Step 1): Validează anunțul → De prospectat / Anunț invalid / Există colaborare', '2️⃣ APEL (Step 2): Contactează proprietarul → Colaborează / Nu colaborează / Nu răspunde', '3️⃣ COLABORARE (Step 3): Programează, semnează mandat → Generează ID proprietate', '4️⃣ PORTOFOLIU (Final): Proprietate activă, gata de promovare și matching', '🚫 BLOCATE: Exclusivitate altă agenție / Închiriat deja / Anunț invalid', '📊 FILTRE: De Verificat / De Apelat / Colaborări / Portofoliu / Blocate', '📈 CONVERSIE: 20-30% leaduri → colaborări → 40-50% colaborări → portofoliu'],
      warnings: ['Actualizează statusul după FIECARE interacțiune', 'Notează detalii în fiecare step - ajută la follow-up', 'Respectă flow-ul - nu sări peste pași']
    }
  }, {
    id: 'best-practices',
    title: 'Best Practices & Tehnici Pro',
    description: 'Strategii avansate pentru prospectare eficientă',
    icon: AwardIcon,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    content: {
      tips: ['🎯 TARGET ZILNIC: 20-30 leaduri procesate = 4-6 colaborări = 2-3 proprietăți în portofoliu', '⏰ TIMING OPTIM: 10-12 și 17-19 pentru apeluri (evită 12-14 și după 20)', '📞 PERSISTENȚĂ: Încearcă de 3 ori la intervale diferite înainte să renunți', '📊 TRACKING: Monitorizează rata de conversie săptămânal și ajustează strategia', '🎤 VOICEMAIL PRO: "Bună ziua, sunt [Nume] agent imobiliar. V-am sunat pentru anunțul din [Zonă]. Vă sun din nou în 2 ore. Mulțumesc!"', '💬 FOLLOW-UP: Proprietarii care nu răspund prima dată pot răspunde a 2-a sau a 3-a', '📸 POZE PROFESIONALE: Investește în poze de calitate - cresc rata de închiriere cu 40%', '🔄 REMATCHING: Proprietățile din portofoliu pot fi matchuite cu multiple leaduri'],
      checklist: ['Procesează minim 20 leaduri pe zi', 'Menține rata de conversie >20% (leaduri → colaborări)', 'Actualizează CRM-ul în timp real', 'Programează follow-up-uri pentru "Nu răspunde"', 'Analizează săptămânal: ce funcționează, ce nu', 'Testează diferite scripturi și abordări', 'Cere feedback de la proprietarii care refuză', 'Celebrează fiecare proprietate adăugată în portofoliu']
    }
  }];
  const currentQuestData = quests[currentQuest];
  const handleCompleteQuest = () => {
    if (!completedQuests.includes(currentQuestData.id)) {
      setCompletedQuests([...completedQuests, currentQuestData.id]);
    }
    if (currentQuest < quests.length - 1) {
      setCurrentQuest(currentQuest + 1);
    }
  };
  const handlePrevious = () => {
    if (currentQuest > 0) {
      setCurrentQuest(currentQuest - 1);
    }
  };
  const progress = completedQuests.length / quests.length * 100;
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-id="element-10795">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-id="element-10796">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-10797">
          <div className="flex items-center justify-between" data-id="element-10798">
            <div className="flex items-center space-x-4" data-id="element-10799">
              <button onClick={() => navigate('/inchiriere/liste-prospectare')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-10800">
                <XIcon className="w-6 h-6 text-slate-600" data-id="element-10801" />
              </button>
              <div data-id="element-10802">
                <h1 className="text-2xl font-bold text-[#132C53]" data-id="element-10803">
                  Tutorial Prospectare
                </h1>
                <p className="text-sm text-slate-600" data-id="element-10804">
                  Quest {currentQuest + 1} din {quests.length}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-10805">
              <div className="text-right" data-id="element-10806">
                <p className="text-xs text-slate-500" data-id="element-10807">Progres</p>
                <p className="text-lg font-bold text-blue-600" data-id="element-10808">
                  {completedQuests.length}/{quests.length}
                </p>
              </div>
              <div className="w-16 h-16 relative" data-id="element-10809">
                <svg className="w-16 h-16 transform -rotate-90" data-id="element-10810">
                  <circle cx="32" cy="32" r="28" stroke="#e2e8f0" strokeWidth="6" fill="none" data-id="element-10811" />
                  <circle cx="32" cy="32" r="28" stroke="#3b82f6" strokeWidth="6" fill="none" strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`} strokeLinecap="round" className="transition-all duration-500" data-id="element-10812" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-10813">
                  <span className="text-sm font-bold text-blue-600" data-id="element-10814">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quest Progress Bar */}
      <div className="bg-white border-b border-slate-200" data-id="element-10815">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-10816">
          <div className="flex items-center space-x-2" data-id="element-10817">
            {quests.map((quest, index) => <Fragment key={quest.id} data-id="element-10818">
                <button onClick={() => setCurrentQuest(index)} className={`flex-1 h-2 rounded-full transition-all ${completedQuests.includes(quest.id) ? 'bg-gradient-to-r from-emerald-500 to-green-600' : index === currentQuest ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-slate-200'}`} data-id="element-10819" />
              </Fragment>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-10820">
        <AnimatePresence mode="wait" data-id="element-10821">
          <motion.div key={currentQuestData.id} initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -50
        }} transition={{
          duration: 0.3
        }} data-id="element-10822">
            {/* Quest Header */}
            <div className="text-center mb-12" data-id="element-10823">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br ${currentQuestData.gradient} rounded-3xl flex items-center justify-center shadow-2xl`} data-id="element-10824">
                <currentQuestData.icon className="w-12 h-12 text-white" data-id="element-10825" />
              </motion.div>
              <h2 className="text-4xl font-bold text-[#132C53] mb-4" data-id="element-10826">
                {currentQuestData.title}
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-10827">
                {currentQuestData.description}
              </p>
            </div>

            {/* Content Cards */}
            <div className="space-y-6" data-id="element-10828">
              {/* Tips */}
              {currentQuestData.content.tips && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200" data-id="element-10829">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-10830">
                    <div className={`w-10 h-10 bg-gradient-to-br ${currentQuestData.gradient} rounded-xl flex items-center justify-center`} data-id="element-10831">
                      <LightbulbIcon className="w-5 h-5 text-white" data-id="element-10832" />
                    </div>
                    <h3 className="text-xl font-bold text-[#132C53]" data-id="element-10833">
                      Sfaturi Esențiale
                    </h3>
                  </div>
                  <div className="space-y-4" data-id="element-10834">
                    {currentQuestData.content.tips.map((tip, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.2 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-10835">
                        <CheckCircleIcon className={`w-5 h-5 text-${currentQuestData.color}-600 flex-shrink-0 mt-0.5`} data-id="element-10836" />
                        <p className="text-slate-700 leading-relaxed" data-id="element-10837">{tip}</p>
                      </motion.div>)}
                  </div>
                </motion.div>}

              {/* Warnings */}
              {currentQuestData.content.warnings && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-8 shadow-lg border-2 border-red-200" data-id="element-10838">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-10839">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center" data-id="element-10840">
                      <AlertCircleIcon className="w-5 h-5 text-white" data-id="element-10841" />
                    </div>
                    <h3 className="text-xl font-bold text-red-900" data-id="element-10842">
                      Atenție - Greșeli Comune
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-10843">
                    {currentQuestData.content.warnings.map((warning, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.4 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-white rounded-xl" data-id="element-10844">
                        <XIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-10845" />
                        <p className="text-red-900 font-medium leading-relaxed" data-id="element-10846">
                          {warning}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}

              {/* Checklist */}
              {currentQuestData.content.checklist && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 shadow-lg border-2 border-emerald-200" data-id="element-10847">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-10848">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center" data-id="element-10849">
                      <ClipboardCheckIcon className="w-5 h-5 text-white" data-id="element-10850" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-900" data-id="element-10851">
                      Checklist Acțiuni
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-10852">
                    {currentQuestData.content.checklist.map((item, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.6 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow cursor-pointer" data-id="element-10853">
                        <div className="w-5 h-5 border-2 border-emerald-500 rounded flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-10854">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-10855" />
                        </div>
                        <p className="text-emerald-900 font-medium leading-relaxed" data-id="element-10856">
                          {item}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12" data-id="element-10857">
              <button onClick={handlePrevious} disabled={currentQuest === 0} className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all ${currentQuest === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-700 hover:bg-slate-50 shadow-md hover:shadow-lg'}`} data-id="element-10858">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-10859" />
                <span data-id="element-10860">Înapoi</span>
              </button>

              {currentQuest === quests.length - 1 ? <button onClick={() => {
              handleCompleteQuest();
              navigate('/inchiriere/liste-prospectare');
            }} className="flex items-center space-x-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-xl hover:scale-105 transition-all" data-id="element-10861">
                  <AwardIcon className="w-5 h-5" data-id="element-10862" />
                  <span data-id="element-10863">Finalizează Tutorial</span>
                </button> : <button onClick={handleCompleteQuest} className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r ${currentQuestData.gradient} text-white hover:shadow-xl hover:scale-105 transition-all`} data-id="element-10864">
                  <span data-id="element-10865">Următorul Quest</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-10866" />
                </button>}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}